import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

interface CaseReportLike {
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  authorId?: string | null;
  reviewerId?: string | null;
  approvedById?: string | null;
  approvalDate?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

interface Props {
  report: CaseReportLike;
}

interface Step {
  id: string;
  label: string;
  filled: boolean;
  active: boolean;
  rejected?: boolean;
}

const formatDate = (iso: string | null | undefined): string => {
  if (iso === null || iso === undefined) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export function CaseReportStatusStrip({ report }: Props) {
  const isApproved = report.status === 'approved';
  const isRejected = report.status === 'rejected';
  const isSubmitted = report.status === 'submitted' || isApproved || isRejected;

  const finalLabel = isRejected ? 'Reject' : 'Approve';
  const finalFilled = isApproved || isRejected;

  const steps: Step[] = [
    { id: 'draft', label: 'Draft', filled: true, active: report.status === 'draft' },
    { id: 'submit', label: 'Submit', filled: isSubmitted, active: report.status === 'submitted' },
    {
      id: 'final',
      label: finalLabel,
      filled: finalFilled,
      active: finalFilled,
      rejected: isRejected,
    },
  ];

  return (
    <ol className="flex items-start justify-between">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const nextFilled = steps[idx + 1]?.filled === true;
        const isRejectedFinal = step.rejected === true;

        let dateLabel = '—';
        if (step.id === 'draft') {
          dateLabel = formatDate(report.createdAt);
        } else if (step.id === 'submit' && isSubmitted) {
          dateLabel = formatDate(report.updatedAt);
        } else if (step.id === 'final' && finalFilled) {
          dateLabel = formatDate(report.approvalDate ?? report.updatedAt);
        }

        // Visual logic — "filled" means the step has happened (use brand mint
        // unless it's the rejected branch, which uses error-50). "Active"
        // means the step is the current resting state (gets a halo ring and a
        // slightly darker ring border to read as "you are here").
        const dotBase =
          step.filled && isRejectedFinal
            ? 'bg-error-500 border-error-500'
            : step.filled
              ? 'bg-primary-400 border-primary-400'
              : 'bg-white border-neutral-300';
        const dotHalo =
          step.active && isRejectedFinal
            ? 'shadow-[0_0_0_4px_rgba(239,68,68,0.18)]'
            : step.active
              ? 'shadow-[0_0_0_4px_rgba(2,97,74,0.18)]'
              : '';

        return (
          <li
            key={step.id}
            aria-current={step.active ? 'step' : undefined}
            className="flex flex-1 flex-col items-center gap-2"
          >
            <div className="flex w-full items-center">
              <div
                className={cn(
                  'flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 transition-shadow',
                  dotBase,
                  dotHalo,
                )}
              >
                {step.filled && !step.active ? (
                  <Check className="h-2 w-2 text-white" strokeWidth={3} />
                ) : null}
              </div>
              {isLast ? null : (
                <div
                  className={cn(
                    'h-0.5 flex-1 rounded-full transition-colors',
                    nextFilled ? 'bg-primary-300' : 'bg-neutral-200',
                  )}
                />
              )}
            </div>
            <span
              className={cn(
                'font-display tracking-eyebrow text-[10.5px] font-medium uppercase italic transition-colors',
                step.active
                  ? isRejectedFinal
                    ? 'text-error-700'
                    : 'text-accent-ink'
                  : step.filled
                    ? 'text-accent-ink/70'
                    : 'text-neutral-400',
              )}
            >
              {step.label}
            </span>
            <span className="font-mono text-[10.5px] text-neutral-500 tabular-nums">
              {dateLabel}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
